/**
 * Pure routing model for the SPA shell (E10-F01-T03).
 *
 * This module is intentionally framework-free and side-effect-free: it knows the
 * set of top-level views, their slug/title and how to (de)serialize a route to a
 * URL path. The React `useRouter` hook (./useRouter.ts) wires this to the real
 * `history` / `popstate` and `route.save` (localStorage). Keeping the logic here
 * makes the navigate/popstate contract unit-testable without a DOM.
 *
 * Parity with the legacy SPA `core.js`:
 *   - one `#view-<id>` container per view; switching = every `#view-*` gets
 *     `hidden`, the target clears it and replays `.fade-in`;
 *   - `navigate()` pushes `history.pushState` + persists the route + updates the
 *     document/page title;
 *   - `popstate` restores the view encoded in the URL (projects/inbox/users/project).
 */

/** Views restored on popstate / deep-link (acceptance criteria explicit set). */
export const VIEW_IDS = [
  'projects',
  'inbox',
  'research',
  'users',
  'project',
  // KS-02 — the contacts directory. Deep-linkable to one client (`/clients/7`),
  // but unlike `project` the id-less form is a REAL screen (the list), so it is
  // not demoted to the default view.
  'clients',
  // E13-F01 — the in-app user guide. A plain id-less view (`/help`), reached
  // from the sidebar FOOTER rather than the NAV_ITEMS list: it is an
  // always-available reference, not a workspace.
  'help',
  // E13-F02/F03 — the admin and Research guides. Reached from cards on
  // `/help` (role-gated there, not here — see `HelpView.tsx`); like every
  // other admin-only screen in this app the route itself is not hard-guarded,
  // only nav-hidden (see decision-log.md, Help E13-F02).
  'help_admin',
  'help_research',
] as const;
export type ViewId = (typeof VIEW_IDS)[number];

/** The default view shown when no/unknown route is present. */
export const DEFAULT_VIEW: ViewId = 'projects';

/** DOM id of a view container, e.g. `view-projects`. */
export function viewElementId(view: ViewId): string {
  return `view-${view}`;
}

/** Human page-title rendered in the sticky header (Playfair) for each view. */
const VIEW_TITLES: Record<ViewId, string> = {
  // FP-07 — v1 workspace heading is "Project Portfolio" (not "Projects").
  projects: 'Project Portfolio',
  inbox: 'Inbox',
  research: 'Research',
  // E06-F04-T12 — the `users` view is the tabbed Settings screen (nav label
  // "Settings"); the shell appends the active tab ("Settings — Address Book").
  users: 'Settings',
  project: 'Project',
  clients: 'Clients',
  help: 'Help',
  help_admin: 'Admin Guide',
  help_research: 'Research Guide',
};

/** Title shown in the header for a route (page-title + document.title source). */
export function titleForView(view: ViewId): string {
  return VIEW_TITLES[view];
}

/** Narrow an arbitrary string to a known `ViewId` (else `undefined`). */
export function asViewId(value: string | null | undefined): ViewId | undefined {
  return (VIEW_IDS as readonly string[]).includes(value ?? '') ? (value as ViewId) : undefined;
}

/**
 * A resolved route: the active view plus an optional entity id (for `project`
 * detail, encoded as `/project/:id`). Detail views without an id fall back to
 * the list — the shell never shows an empty detail container.
 */
export interface Route {
  readonly view: ViewId;
  readonly id?: string;
}

/** Serialize a route to a URL path (`/projects`, `/project/42`). */
export function routeToPath(route: Route): string {
  if ((route.view === 'project' || route.view === 'clients') && route.id) {
    return `/${route.view}/${encodeURIComponent(route.id)}`;
  }
  return `/${route.view}`;
}

/**
 * Parse a URL path back into a `Route` (the `popstate` / deep-link contract).
 * Unknown paths resolve to the default view so a stale/garbage URL never leaves
 * the shell blank. `/project/:id` keeps its id; `/project` with no id is demoted
 * to the projects list.
 */
export function pathToRoute(pathname: string): Route {
  const segments = pathname.split('/').filter(Boolean);
  const head = asViewId(segments[0]);
  if (!head) {
    return { view: DEFAULT_VIEW };
  }
  if (head === 'project') {
    const id = segments[1] ? decodeURIComponent(segments[1]) : undefined;
    return id ? { view: 'project', id } : { view: DEFAULT_VIEW };
  }
  if (head === 'clients') {
    // `/clients` without an id is the directory LIST — a valid screen, so no demotion.
    const id = segments[1] ? decodeURIComponent(segments[1]) : undefined;
    return id ? { view: 'clients', id } : { view: 'clients' };
  }
  return { view: head };
}

/** Build a `history.state` payload for a route (round-trips through popstate). */
export function routeToState(route: Route): { view: ViewId; id?: string } {
  return route.id ? { view: route.view, id: route.id } : { view: route.view };
}

/** Re-hydrate a route from a `history.state` payload (with path fallback). */
export function stateToRoute(state: unknown, pathname: string): Route {
  if (state && typeof state === 'object' && 'view' in state) {
    const view = asViewId((state as { view?: unknown }).view as string);
    if (view) {
      const rawId = (state as { id?: unknown }).id;
      const id = typeof rawId === 'string' && rawId.length > 0 ? rawId : undefined;
      if (view === 'project') {
        // Mirror pathToRoute: a project detail without a valid id is demoted to
        // the default list so the shell never restores an empty detail container.
        return id ? { view, id } : { view: DEFAULT_VIEW };
      }
      // Other views (e.g. inbox) carry `id` only as an optional deep-select hint
      // (ProjectDetailHeader's "Created from email" link) — preserve it when
      // present instead of always dropping it.
      return id ? { view, id } : { view };
    }
  }
  return pathToRoute(pathname);
}
